<template>
  <div class="cost-center-attribution-sidebar">
    <div class="sidebar-header mb-4">
      <h3>{{ $t('cost-center.cost-centers-list') }}</h3>
      <v-btn
        small
        color="primary"
        :disabled="!!assignMode"
        @click="openCreateDialog"
      >
        <v-icon left small>mdi-plus</v-icon>
        {{ $t('cost-center.add') }}
      </v-btn>
    </div>

    <!-- Search -->
    <v-text-field
      v-model="search"
      :label="$t('search')"
      prepend-inner-icon="mdi-magnify"
      :disabled="!!assignMode"
      clearable
      hide-details
      outlined
      dense
      class="mb-4"
    />

    <!-- Loading -->
    <v-progress-linear v-if="loading" indeterminate class="mb-2" />

    <!-- Cost Centers List -->
    <div class="cost-centers-list">
      <div
        v-for="costCenter in filteredCostCenters"
        :key="costCenter.id"
        class="cost-center-item"
        :class="{
          'selected': costCenter.id === selectedCostCenterId,
          'disabled': assignMode && costCenter.id !== selectedCostCenterId
        }"
        @click="assignMode ? null : selectCostCenter(costCenter)"
      >
        <div class="cost-center-header">
          <div class="cost-center-name">{{ costCenter.name }}</div>
          <v-btn
            v-if="!assignMode"
            icon
            x-small
            @click.stop="openEditDialog(costCenter)"
          >
            <v-icon x-small>mdi-pencil</v-icon>
          </v-btn>
        </div>

        <div class="cost-center-stats-and-actions">
          <div class="cost-center-stats">
            <v-chip x-small class="mr-1">
              <v-icon left x-small>mdi-account</v-icon>
              {{ costCenter.membersCount || 0 }}
            </v-chip>
            <v-chip x-small class="mr-1">
              <v-icon left x-small>mdi-ev-station</v-icon>
              {{ costCenter.chargingPointsCount || 0 }}
            </v-chip>
            <v-chip x-small>
              <v-icon left x-small>mdi-gauge</v-icon>
              {{ costCenter.meterDevicesCount || 0 }}
            </v-chip>
          </div>

          <!-- Action buttons when this cost center is selected -->
          <div v-if="costCenter.id === selectedCostCenterId" class="cost-center-actions">
            <!-- Normal mode: show Assign/Unassign buttons -->
            <template v-if="!assignMode">
              <v-btn x-small dense color="primary" @click.stop="$emit('start-assign')">
                <v-icon x-small left>mdi-link-plus</v-icon>
                <span class="action-text">{{ $t('cost-center.assign') }}</span>
              </v-btn>
              <v-btn
                v-if="hasEntities(costCenter)"
                x-small
                dense
                color="error"
                @click.stop="$emit('start-unassign')"
              >
                <v-icon x-small left>mdi-link-off</v-icon>
                <span class="action-text">{{ $t('cost-center.unassign') }}</span>
              </v-btn>
            </template>

            <!-- Assign/Unassign mode: show Cancel/Apply buttons -->
            <template v-else>
              <v-btn x-small dense @click.stop="$emit('cancel-mode')" :disabled="operationInProgress">
                <v-icon x-small left>mdi-close</v-icon>
                <span class="action-text">{{ $t('cancel') }}</span>
              </v-btn>
              <v-btn
                x-small
                dense
                color="primary"
                @click.stop="$emit('apply-mode')"
                :disabled="selectedEntitiesCount === 0 || operationInProgress"
                :loading="operationInProgress"
              >
                <v-icon x-small left>mdi-check</v-icon>
                <span class="action-text">{{ $t('apply') }} ({{ selectedEntitiesCount }})</span>
              </v-btn>
            </template>
          </div>
        </div>
      </div>

      <!-- Empty state -->
      <v-alert v-if="filteredCostCenters.length === 0 && !loading" type="info" dense text class="mt-4">
        {{ search ? $t('no-results') : $t('cost-center.no-cost-centers') }}
      </v-alert>
    </div>

    <!-- Edit Cost Center Dialog -->
    <v-dialog v-model="showEditDialog" max-width="500">
      <v-card>
        <v-card-title>{{ $t('cost-center.edit-cost-center') }}</v-card-title>
        <v-card-text>
          <v-text-field
            v-model="editingCostCenter.name"
            :label="$t('cost-center.name')"
            outlined
            dense
            :rules="[v => !!v || $t('required')]"
          />
        </v-card-text>
        <v-card-actions>
          <v-btn text small color="error" @click="confirmDelete">
            <v-icon left small>mdi-delete</v-icon>
            {{ $t('delete') }}
          </v-btn>
          <v-spacer />
          <v-btn text small @click="cancelEdit">
            {{ $t('cancel') }}
          </v-btn>
          <v-btn color="primary" small @click="saveEdit">
            {{ $t('save') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="showDeleteDialog" max-width="400">
      <v-card>
        <v-card-title>{{ $t('cost-center.delete-cost-center') }}</v-card-title>
        <v-card-text>
          {{ $t('cost-center.delete-confirmation', { name: editingCostCenter.name }) }}
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text small @click="showDeleteDialog = false">
            {{ $t('cancel') }}
          </v-btn>
          <v-btn color="error" small @click="deleteConfirmed">
            {{ $t('delete') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Create Cost Center Dialog -->
    <v-dialog v-model="showCreateDialog" max-width="500">
      <v-card>
        <v-card-title>{{ $t('cost-center.create-cost-center') }}</v-card-title>
        <v-card-text>
          <v-text-field
            v-model="newCostCenter.name"
            :label="$t('cost-center.name')"
            outlined
            dense
            :rules="[v => !!v || $t('required')]"
            autofocus
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text small @click="cancelCreate">
            {{ $t('cancel') }}
          </v-btn>
          <v-btn color="primary" small @click="saveCreate" :disabled="!newCostCenter.name">
            {{ $t('create') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import CostCenterRepository from '@repository/CostCenterRepository'

export default {
  props: {
    organizationId: {
      type: String,
      required: true
    },
    selectedCostCenterId: {
      type: String,
      default: null
    },
    selectedEntitiesCount: {
      type: Number,
      default: 0
    },
    assignMode: {
      type: String,
      default: null // null, 'assign', or 'unassign'
    },
    operationInProgress: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      costCenters: [],
      loading: true, // Start with loading = true
      search: '',
      showEditDialog: false,
      showDeleteDialog: false,
      showCreateDialog: false,
      editingCostCenter: {
        id: null,
        name: ''
      },
      newCostCenter: {
        name: ''
      }
    }
  },

  computed: {
    filteredCostCenters() {
      if (!this.search) {
        return this.costCenters
      }

      const searchLower = this.search.toLowerCase()
      return this.costCenters.filter(cc =>
        cc.name.toLowerCase().includes(searchLower)
      )
    }
  },

  mounted() {
    this.loadCostCenters()
  },

  methods: {
    loadCostCenters() {
      this.loading = true

      CostCenterRepository.search(this.organizationId, {
        offset: 0,
        limit: 1000
      }, 'attribution-sidebar').then(result => {
        this.costCenters = result.items || []
        this.loading = false
      }).catch(err => {
        this.notifyError(err)
        this.loading = false
      })
    },

    selectCostCenter(costCenter) {
      this.$emit('select', this.selectedCostCenterId === costCenter.id ? null : costCenter)
    },

    hasEntities(costCenter) {
      const total = (costCenter.membersCount || 0) +
                    (costCenter.chargingPointsCount || 0) +
                    (costCenter.meterDevicesCount || 0)
      return total > 0
    },

    openEditDialog(costCenter) {
      this.editingCostCenter = {
        id: costCenter.id,
        name: costCenter.name
      }
      this.showEditDialog = true
    },

    cancelEdit() {
      this.showEditDialog = false
      this.editingCostCenter = { id: null, name: '' }
    },

    saveEdit() {
      if (!this.editingCostCenter.name) {
        this.$dialog.notify.warning(this.$t('cost-center.name-required'))
        return
      }

      // TODO: Call API to update cost center
      CostCenterRepository.update(this.organizationId, this.editingCostCenter.id, {
        name: this.editingCostCenter.name
      }).then(() => {
        this.notifySuccess(this.$t('cost-center.updated-successfully'))
        this.loadCostCenters()
        this.showEditDialog = false
        this.editingCostCenter = { id: null, name: '' }
        this.$emit('cost-center-updated')
      }).catch(err => {
        this.notifyError(err)
      })
    },

    confirmDelete() {
      this.showDeleteDialog = true
    },

    deleteConfirmed() {
      CostCenterRepository.delete(this.organizationId, this.editingCostCenter.id).then(() => {
        this.$dialog.notify.success(this.$t('cost-center.deleted-successfully'))
        this.loadCostCenters()
        this.showDeleteDialog = false
        this.showEditDialog = false
        this.editingCostCenter = { id: null, name: '' }
        this.$emit('select', null) // Deselect if it was selected
        this.$emit('cost-center-deleted')
      }).catch(err => {
        this.notifyError(err)
      })
    },

    openCreateDialog() {
      this.newCostCenter = { name: '' }
      this.showCreateDialog = true
    },

    cancelCreate() {
      this.showCreateDialog = false
      this.newCostCenter = { name: '' }
    },

    saveCreate() {
      if (!this.newCostCenter.name) {
        this.$dialog.notify.warning(this.$t('cost-center.name-required'))
        return
      }

      CostCenterRepository.create(this.organizationId, {
        name: this.newCostCenter.name
      }).then(() => {
        this.$dialog.notify.success(this.$t('cost-center.created-successfully'))
        this.loadCostCenters()
        this.showCreateDialog = false
        this.newCostCenter = { name: '' }
        this.$emit('cost-center-created')
      }).catch(err => {
        this.notifyError(err)
      })
    }
  }
}
</script>

<style lang="scss">
.cost-center-attribution-sidebar {
  .sidebar-header {
    display: flex;
    justify-content: space-between;
    align-items: center;

    h3 {
      margin: 0;
      font-size: 16px;
      font-weight: 600;
    }
  }

  .cost-centers-list {
    max-height: calc(100vh - 400px);
    overflow-y: auto;
  }

  .cost-center-item {
    padding: 12px;
    margin-bottom: 8px;
    border: 1px solid #e0e0e0;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
      background-color: #f5f5f5;
    }

    &.selected {
      background-color: #f5f5f5;
      border-left: 4px solid #E53C22;
    }

    &.disabled {
      opacity: 0.4;
      cursor: not-allowed;
      pointer-events: none;

      &:hover {
        background-color: transparent;
        border-color: #e0e0e0;
      }
    }

    .cost-center-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 8px;

      .cost-center-name {
        font-weight: 500;
        font-size: 14px;
      }
    }

    .cost-center-stats-and-actions {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    .cost-center-stats {
      display: flex;
      gap: 4px;
    }

    .cost-center-actions {
      display: flex;
      gap: 4px;
      align-items: center;

      .action-text {
        font-size: 11px;
        text-transform: none;
      }
    }
  }
}
</style>
